import PropTypes from 'prop-types'
import React, { useContext } from 'react'
import { noop } from 'lodash'
import Body from './Body'
import Footer from './Footer'
import Header from './Header'
import ModalSidebarContext from './ModalSidebarContext'
import { TabProvider } from './TabContext'
import useFooterButtons from './useFooterButtons'

const propTypes = {
  /**
   * Modal panel content
   */
  children: PropTypes.node,
  /**
   * Left button props (label, onClick, variant, etc)
   * Instead of an object you can also pass a string directly for common use cases: 'cancel', 'close', 'back', 'next', 'validateThenProceed' (see useFooterButtons)
   */
  leftButton: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  /**
   * Right button props (label, onClick, variant, etc), or a rendered action group.
   * Instead of an object you can also pass a string directly for common use cases: 'cancel', 'close', 'back', 'next', 'validateThenProceed' (see useFooterButtons)
   */
  rightButton: PropTypes.oneOfType([PropTypes.object, PropTypes.string, PropTypes.node]),
  /**
   * Async function to validate the panel fields, necessary if you are using the 'validateThenProceed' buttons
   * Should return a boolean indicating if the fields have errors. Please memoize it.
   */
  onValidate: PropTypes.func,
  /**
   * Indicates if 'next' button should be disabled
   */
  disableNext: PropTypes.bool,
  /**
   * If passed, renders tabs on modal headers. Tabs are a way to change content in the same panel (different from the steps which are meant to switch the panel)
   */
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.node,
      key: PropTypes.string,
    }),
  ),
  /**
   * Initial preference for uncontrolled tabs: selects this tab on open if available, otherwise the first tab.
   * Later changes do not replace an available current selection.
   */
  initialTabId: PropTypes.string,
  /**
   * Opts into controlled tabs when not undefined. Updates the selection while the panel stays mounted.
   * Unavailable IDs (including null) fall back to the first available tab; initialTabId is ignored.
   */
  activeTabId: PropTypes.string,
  /**
   * Receives an available tab ID on selection and on controlled fallback (null if there are no tabs).
   * Controlled callers should update activeTabId in response. Prop updates to available tabs do not notify.
   */
  onTabChange: PropTypes.func,
  /**
   * Rendered right after the tabs, e.g. a loading indicator while more tabs may appear
   */
  tabsAddon: PropTypes.node,
}

/**
 * Connects Header, Body and Footer with the modal context provided by ModalSidebar
 * If you don't want all the features you can always import the individual (Modal, Body, Header, Footer) from this folder
 */
function ModalPanel({
  children = null,
  leftButton = {},
  rightButton = {},
  onValidate = noop,
  disableNext = false,
  tabs = [],
  initialTabId = null,
  activeTabId = undefined,
  onTabChange = null,
  tabsAddon = null,
}) {
  const {
    onHideWithConfirmation,
    steps,
    currentStepIndex,
    nextStep,
    previousStep,
    showValidationErrorBanner,
    setShowValidationErrorBanner,
    title,
    subTitles,
    variant,
    isLoading,
    bodyClassName,
    loadingDescription,
    titlePillLabel,
    headerBackButtonLabel,
    headerBackButtonAction,
    isStillProcessing,
  } = useContext(ModalSidebarContext)

  const buttons = useFooterButtons({
    onHideWithConfirmation,
    nextStep,
    previousStep,
    setShowValidationErrorBanner,
    onValidate,
    disableNext,
  })

  return (
    <TabProvider tabs={tabs} initialTabId={initialTabId} activeTabId={activeTabId} onTabChange={onTabChange}>
      <Header
        title={title}
        subTitles={subTitles}
        showValidationErrorBanner={showValidationErrorBanner}
        variant={variant}
        steps={steps}
        currentStepIndex={currentStepIndex}
        titlePillLabel={titlePillLabel}
        backButtonLabel={headerBackButtonLabel || (headerBackButtonAction ? 'GENERAL.BACK' : null)}
        backButtonAction={headerBackButtonAction || onHideWithConfirmation}
        tabsAddon={tabsAddon}
      />

      <Body isLoading={isLoading} loadingDescription={loadingDescription} className={bodyClassName}>
        {children}
      </Body>
      <Footer
        modalVariant={variant}
        isLoading={isLoading}
        // While the request is still processing (useReportStillProcessing), only Close: Back or Save would send it again
        leftButton={isStillProcessing ? null : buttons[leftButton] || leftButton} // if leftButton is a string will try to match one of the builtin buttons from 'useFooterButtons', otherwise will use leftButton as it is
        rightButton={isStillProcessing ? buttons.close : buttons[rightButton] || rightButton}
      />
    </TabProvider>
  )
}

ModalPanel.propTypes = propTypes

export default ModalPanel
